import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { toast } from "sonner";
import { CheckCircle2, XCircle } from "lucide-react";

export default function AdminClaims() {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/claims").then(r => setClaims(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const decide = async (id, status) => {
    try {
      await api.post(`/admin/claims/${id}/decide`, { status });
      toast.success(status === "approved" ? "Claim approved — owner assigned" : "Claim rejected");
      load();
    } catch (e) {
      toast.error("Could not update claim");
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <h1 className="font-heading text-3xl sm:text-4xl font-bold text-gray-900">Owner claims</h1>
      <p className="mt-3 text-gray-700 max-w-2xl">Review claim requests from users. Approving a claim assigns them as the venue owner and lets them edit the listing.</p>

      <Card className="mt-8 rounded-xl border border-gray-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Venue</TableHead>
              <TableHead>Claimant</TableHead>
              <TableHead>Note</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-56"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={5} className="text-center text-gray-500 py-8">Loading…</TableCell></TableRow>}
            {!loading && claims.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-gray-500 py-8">No claims yet.</TableCell></TableRow>}
            {claims.map(c => (
              <TableRow key={c.id}>
                <TableCell>
                  <Link to={`/venue/${c.venue_id}`} className="font-medium hover:underline">{c.venue_name || c.venue_id}</Link>
                </TableCell>
                <TableCell>
                  <div className="font-medium">{c.user_name}</div>
                  <div className="text-xs text-gray-500">{c.user_email}</div>
                  {c.contact_name && <div className="text-xs text-gray-500 mt-1">Contact: {c.contact_name}</div>}
                </TableCell>
                <TableCell className="text-sm text-gray-700 max-w-xs whitespace-pre-line">{c.note}</TableCell>
                <TableCell><Badge variant="outline" className="rounded-full capitalize">{c.status}</Badge></TableCell>
                <TableCell className="space-x-2">
                  {c.status === "pending" && (
                    <>
                      <Button size="sm" onClick={() => decide(c.id, "approved")} data-testid={`approve-claim-${c.id}`} className="bg-emerald-600 hover:bg-emerald-700"><CheckCircle2 className="w-4 h-4 mr-1"/>Approve</Button>
                      <Button size="sm" variant="outline" onClick={() => decide(c.id, "rejected")} data-testid={`reject-claim-${c.id}`}><XCircle className="w-4 h-4 mr-1"/>Reject</Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
